import type { QueryKey } from "@tanstack/react-query";

/** Query-key factory formalizing the module's existing `["commissions", …]`
 * literals (shapes preserved verbatim so prefix invalidations keep matching).
 * `commissionKeys.all` covers every commissions read. */
export const commissionKeys = {
  all: ["commissions"] as const,
  claim: (id: string) => [...commissionKeys.all, "claim", id] as const,
  claimPosition: (id: string): QueryKey => [...commissionKeys.claim(id), "position"],
  claimComments: (id: string): QueryKey => [...commissionKeys.claim(id), "comments"],
  // SP-5 WS-3 — the revenue-confirmation funding picker's candidate list.
  fundingCandidates: (id: string): QueryKey => [...commissionKeys.claim(id), "funding-candidates"],
  queues: () => [...commissionKeys.all, "queue"] as const,
  queue: (tab: string): QueryKey => [...commissionKeys.queues(), tab],
  queueStats: (): QueryKey => [...commissionKeys.queues(), "stats"],
  mineStats: (year: number): QueryKey => [...commissionKeys.all, "mine", "stats", year],
  mineClaims: (statusFilter: string): QueryKey =>
    [...commissionKeys.all, "claims", "mine", statusFilter],
  periods: (): QueryKey => [...commissionKeys.all, "periods"],
  pools: () => [...commissionKeys.all, "pools"] as const,
  poolList: (scope: "all" | "active"): QueryKey => [...commissionKeys.pools(), scope],
  // SP-5 WS-4 — pool summary (income + allocation state) for the Pool F teaching state.
  poolSummary: (): QueryKey => [...commissionKeys.all, "pool-summary"],
  templates: () => [...commissionKeys.all, "templates"] as const,
  templatesMine: (): QueryKey => [...commissionKeys.templates(), "mine"],
  managerOverview: (year: number): QueryKey => [...commissionKeys.all, "manager-overview", year],
  escalations: (): QueryKey => [...commissionKeys.all, "escalations"],
  suggested: (): QueryKey => [...commissionKeys.all, "suggested", "mine"],
  similar: (poolId: string): QueryKey => [...commissionKeys.all, "similar", poolId],
  rates: () => [...commissionKeys.all, "rates"] as const,
  allocations: (): QueryKey => [...commissionKeys.rates(), "allocations"],
  rateGrid: (): QueryKey => [...commissionKeys.rates(), "grid"],
  /** Picker source for the allocation editor. Under `rates()` so the existing
   *  allocation-mutation invalidations sweep it too — it is a static list, so
   *  the occasional extra refetch is free. */
  allocationPositions: (): QueryKey => [...commissionKeys.rates(), "positions"],
  allocationDeleteImpact: (id: string): QueryKey =>
    [...commissionKeys.allocations(), "delete-impact", id],

  // L7/U7 — one loan's frozen commission attribution, as the correction
  // surface reads it. Under `all` so the correction's own invalidation, which
  // has to sweep every claim surface anyway, sweeps this too: a correction
  // that re-pointed claims and left the row it corrected showing the OLD
  // values is the one refetch nobody would think to force.
  loanAttribution: (loanId: string): QueryKey =>
    [...commissionKeys.all, "loan-attribution", loanId],

  // WS7 — Admin payment ledger (/admin/commissions/payments) lives under a
  // SEPARATE prefix root ("admin", not "commissions") because it bundles
  // the cross-claimant browse views. Mutations that touch CommissionPayment
  // rows must invalidate BOTH commissionKeys.all (the per-claim/lifecycle
  // surfaces) AND adminPayments() (the admin ledger + summary tiles), or
  // the YTD/MTD/Filtered tiles serve stale-0 from cache for 5 minutes.
  adminPayments: () => ["admin", "commissions", "payments"] as const,
  adminPaymentList: (query: unknown): QueryKey =>
    [...commissionKeys.adminPayments(), query],
  adminPaymentAggregates: (query: unknown): QueryKey =>
    [...commissionKeys.adminPayments(), "aggregates", query],
};
